'use client';

import { Header, Media } from '@/payload-types';
import { usePathname } from 'next/navigation';
import Script from 'next/script';
import { useEffect, useRef, useState } from 'react';

import { MediaComponent } from '@/components/Media';
import { ChevronDown } from 'lucide-react';
import { clsx } from 'clsx';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useCookies } from 'react-cookie';

declare global {
  interface Window {
    googleTranslateElementInit: Function;
    google: any;
  }
}

interface IHeaderLanguageSelectorProps {
  language: Exclude<Header['firstRow'], undefined>['language'];
}

export default function HeaderLanguageSelector(props: IHeaderLanguageSelectorProps) {
  const { language = [] } = props;
  const currentPath = usePathname();

  const [currentLang, setCurrentLang] = useState(language?.[0]);
  const [cookies] = useCookies(['googtrans']);

  const [expanded, setExpanded] = useState(false);
  const toggleExpand = () => setExpanded(!expanded);

  const setLangWrapper = (
    lang: Exclude<IHeaderLanguageSelectorProps['language'], undefined | null>[number],
  ) => {
    return () => {
      setCurrentLang(lang);
      setExpanded(false);
      window.location.href = `${currentPath}#googtrans(vi|${lang?.value || 'vi'})`;
      location.reload();
    };
  };

  useEffect(() => {
    const lang = cookies.googtrans; // Should return a value like "/vi/en"
    if (!lang) {
      return setCurrentLang(language?.[0]);
    }

    const selectedLang = lang.split('/')[2];
    const selectedLangObject = language?.find(lang => lang.value === selectedLang);
    if (selectedLangObject) {
      setCurrentLang(selectedLangObject);
    }
  }, [cookies, language]);

  const onLoad = () => {
    window.googleTranslateElementInit = () => {
      new window.google.translate.TranslateElement(
        {
          autoDisplay: false,
          layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE
        },
        'google_translate_element',
      );
    };
    const interval = setInterval(() => {
      const iframe = document?.getElementById(':1.container');
      if (iframe) {
        iframe.setAttribute('title', 'Google Translate Frame');
        clearInterval(interval);
      }
    }, 100);
  };

  const ref = useRef<HTMLDivElement>(null);

  useClickOutside(ref, outside => {
    if (outside) setExpanded(false);
  });

  return (
    <div className="relative md:bg-white w-fit px-3 md:px-0" ref={ref}>
      <div id="google_translate_element" style={{ display: 'none' }}></div>
      <Script
        strategy="afterInteractive"
        src="//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit"
        onLoad={onLoad}
        async
      />
      <button
        className="cursor-pointer flex items-center rounded px-1 transition-all duration-500 gap-1 bg-transparent hover:bg-neutral-200"
        onClick={toggleExpand}
      >
        <MediaComponent
          resource={{
            ...({ ...(currentLang?.flag as Media), alt: '' } as Media),
            height: 20,
            width: 20,
          }}
          placeholder="empty"
          imgClassName="object-cover rounded w-6 h-5"
        />
        <span className="notranslate">{currentLang?.label}</span>
        <ChevronDown
          height={16}
          className={clsx('transition-all duration-500', expanded && '-rotate-180')}
        />
      </button>
      <div
        className={clsx(
          'w-auto rounded bg-white border-neutral-200 absolute md:left-0 grid grid-rows-0fr transition-all duration-500 z-10 shadow',
          expanded && 'grid-rows-1fr border',
        )}
      >
        <ul className="overflow-hidden">
          {language?.map(lang => (
            <li key={lang.id}>
              <button
                onClick={setLangWrapper(lang)}
                className={clsx(
                  'w-full cursor-pointer flex items-center rounded px-1 transition-all duration-500 gap-1 hover:bg-neutral-200',
                  currentLang?.id === lang.id && 'bg-neutral-200',
                )}
              >
                <MediaComponent
                  resource={{
                    ...({ ...(lang?.flag as Media), alt: '' } as Media),
                    height: 20,
                    width: 20,
                  }}
                  placeholder="empty"
                  imgClassName="object-cover rounded w-6 h-5"
                />
                <span className="whitespace-nowrap notranslate">{lang?.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
